import { z } from "zod";
import apiClient from "../../../shared/api/client";
import type {
  ListPanelsQuery,
  ListPanelsResponse,
  ModeratePanelBody,
  Panel,
} from "../types";

const panelCategorySchema = z.enum(["lower_third", "label", "stats"]);
const panelLicenseSchema = z.enum(["free", "paid", "community"]);
const panelStatusSchema = z.enum(["pending", "approved", "rejected"]);

const panelSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  version: z.string(),
  status: panelStatusSchema,
  category: panelCategorySchema,
  license: panelLicenseSchema,
  priceArs: z.number().nullable().optional(),
  authorId: z.string(),
  authorName: z.string(),
  description: z.string().nullable().optional(),
  tags: z.array(z.string()).default([]),
  installCount: z.number().default(0),
  previewThumbnailUrl: z.string().nullable().optional(),
  previewAnimatedUrl: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  template: z.unknown(),
});

const listPanelsResponseSchema = z.object({
  items: z.array(panelSchema),
  total: z.number(),
});

export const listPanels = async (
  query: ListPanelsQuery = {},
): Promise<ListPanelsResponse> => {
  // Cola de moderación: ruta admin (todos los estados, filtro `status`). La pública
  // (`/panels`) sólo lista approved y rechaza `status` con 400.
  const response = await apiClient.get("/admin/panels", { params: query });
  const raw = response.data as Record<string, unknown> | undefined;
  const candidate =
    raw && "items" in raw
      ? raw
      : { items: raw?.data ?? [], total: raw?.total ?? 0 };
  const parsed = listPanelsResponseSchema.parse(candidate);
  return parsed as ListPanelsResponse;
};

export const getPanelBySlug = async (slug: string): Promise<Panel> => {
  const response = await apiClient.get(`/panels/${slug}`);
  const parsed = panelSchema.parse(response.data);
  return parsed as Panel;
};

export const moderatePanel = async (
  id: string,
  body: ModeratePanelBody,
): Promise<void> => {
  await apiClient.post(`/admin/panels/${id}/moderate`, body);
};

export const panelsApi = {
  listPanels,
  getPanelBySlug,
  moderatePanel,
};
